import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check } from 'react-bootstrap-icons';
import { AI_CHAT_FONT_SIZE, AI_CHAT_LINE_HEIGHT } from './aiChatMarkdownComponents';

function isDarkSurface(hex) {
  const raw = typeof hex === 'string' ? hex.trim() : '';
  const match = raw.match(/^#([0-9a-f]{6})$/i);
  if (!match) return true;
  const n = parseInt(match[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 < 150;
}

/** Assistant replies sit on a lifted surface so they separate from the page. */
export function assistantBubbleColors(colors = {}) {
  if (isDarkSurface(colors.bgPrimary)) {
    return {
      backgroundColor: '#222B3E',
      color: colors.text || '#E8EDF4',
      border: '1px solid #31405A',
    };
  }
  return {
    backgroundColor: '#FFFFFF',
    color: colors.text || '#1A202C',
    border: `1px solid ${colors.border || '#D6DCE6'}`,
  };
}

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy to clipboard"
      style={{
        position: 'absolute',
        top: '6px',
        right: '6px',
        background: 'rgba(0,0,0,0.35)',
        border: 'none',
        borderRadius: '4px',
        color: copied ? '#4ade80' : 'rgba(255,255,255,0.7)',
        cursor: 'pointer',
        padding: '3px 5px',
        display: 'flex',
        alignItems: 'center',
        transition: 'color 0.2s ease',
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
};

export default function AIChatTranscript({
  messages = [],
  isLoading = false,
  markdownComponents,
  colors,
}) {
  return messages.map((message, index) => {
    const isStreaming = Boolean(message.streaming) && isLoading;
    const isUser = message.role === 'user';
    const assistant = assistantBubbleColors(colors);
    return (
      <div
        key={index}
        className={isUser ? 'ai-chat-message ai-chat-message--user' : 'ai-message-bubble ai-chat-message'}
        data-testid={isStreaming ? 'ai-chat-streaming' : `ai-chat-${message.role}`}
        style={{
          alignSelf: isUser ? 'flex-end' : 'flex-start',
          backgroundColor: isUser ? colors.primary : assistant.backgroundColor,
          border: isUser ? 'none' : assistant.border,
          padding: isUser ? '12px 14px' : '16px 16px 14px',
          borderRadius: '12px',
          borderBottomRightRadius: isUser ? '4px' : '12px',
          borderBottomLeftRadius: isUser ? '12px' : '4px',
          color: isUser ? colors.white : assistant.color,
          fontSize: `${AI_CHAT_FONT_SIZE}px`,
          lineHeight: String(AI_CHAT_LINE_HEIGHT),
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          position: 'relative',
        }}
      >
        {message.role === 'assistant' && !isStreaming && (
          <span className="ai-copy-btn">
            <CopyButton text={message.content} />
          </span>
        )}
        {message.role === 'assistant' ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {message.content}
          </ReactMarkdown>
        ) : (
          message.content
        )}
        {isStreaming ? <span className="ai-chat-stream-caret" aria-hidden="true" /> : null}
      </div>
    );
  });
}
